import {combineLatest, of, ReplaySubject, zip} from 'rxjs';
import { timer } from 'rxjs';

import {Injectable, OnDestroy} from '@angular/core';
import {TranslateProxy} from './translate.proxy';
import {Observable} from 'rxjs';
import {filter, map, mergeMap, share} from 'rxjs/operators';
import {takeUntilDestroy} from 'take-until-destroy';
import {
  ACTION_SUFFIX, KIOSK_SPS_CODE_DESCRIPTION_PREFIX,
  KIOSK_SUFFIX,
  LABEL_SUFFIX,
  LOGIN_SUFFIX, LOOKUP_DATA_SUFFIX,
  MESSAGE_SUFFIX, ORG_STRUCTURE_DESCRIPTION_PREFIX, ORG_STRUCTURE_LOCATION_DESCRIPTION_PREFIX,
  PROCEDURE_CODE_DESCRIPTION_PREFIX, SPS_CODE_DESCRIPTION_PREFIX, TITLE_SUFFIX
} from '../translate.suffix';
import {IntlServicePP} from '../../intl/services/intl.service';
import {ITranslatedItem} from '../interfaces/translated.item';
import { LogManager } from 'src/common/log/log.manager';
import { LogCat } from 'src/common/log/log.categories';

@Injectable({
	providedIn: 'root'
})
export class TranslateService implements OnDestroy {
	public preLoginDictionary: any = {};
	public dictionary: any = {};

	private getFromServerTimer: Observable<number>;
	private waitingForServerTranslationsObs: Observable<string[]>;
	private keysFromServer: string[];
	private preLoginDicInited = new ReplaySubject(1);

	// DEBUG
	private missingTranslations: {[key: string]: string[]} = {};

	constructor(private translateProxy: TranslateProxy, private intlServicePP: IntlServicePP) {
		this.translateProxy.getPreLoginTranslations().pipe(takeUntilDestroy(this)).subscribe(res => {
			this.preLoginDictionary = res;
			this.preLoginDicInited.next();
		}, (error) => {
			LogManager.debug(LogCat.SelfScheduling.General, "Unknown error", error);
		});

		this.intlServicePP.getUsedLanguageObservable().pipe(takeUntilDestroy(this)).subscribe(lang => {
			if (lang && !this.dictionary[lang.languageKey.toString()]) {
				this.getTranslationsFromServer(lang.languageKey.toString()).pipe(takeUntilDestroy(this)).subscribe(dictionary => {
					this.dictionary[lang.languageKey.toString()] = this.dictionary[lang.languageKey.toString()] ?
						Object.assign(this.dictionary[lang.languageKey.toString()], dictionary) : dictionary;
					this.intlServicePP.setLanguage(lang, false, lang.manualSelect);
				}, (error) => {
					LogManager.debug(LogCat.SelfScheduling.General, "Unknown error", error);
				});
			}
		});
	}

	ngOnDestroy(): void { }

	public transformLogin(key: string, ...args: any[]): Observable<string> {
		return combineLatest([
			this.preLoginDicInited.asObservable(),
			this.intlServicePP.getUsedLanguageObservable().pipe(filter(currLanguageSelection => currLanguageSelection.manualSelect))
		]).pipe(map(preLoginAndlang => {
			const lang = preLoginAndlang[1];
			const currDict = this.preLoginDictionary[lang.languageKey.toString()];
			return currDict ? (currDict[key] ? this.stringFormatter(currDict[key], args) : '[' + key + ']') : '';
		}));
	}

	public transform(key: string, ...args: any[]): Observable<string> {
		if (key.indexOf(LOGIN_SUFFIX) > -1) {
			return  this.transformLogin(key, args);
		} else {
			return combineLatest([this.preLoginDicInited.asObservable(), this.intlServicePP.getUsedLanguageObservable()])
				.pipe(map(preLoginAndlang => {
					const lang = preLoginAndlang[1];
					const currDict = this.dictionary[lang.languageKey.toString()];

					// DEBUG
					if (currDict && !currDict[key] && !this.missingTranslations.hasOwnProperty(key)) {
						this.missingTranslations[key] = args;
						console.debug("Missing translations", this.missingTranslations);
					}
					// DEBUG

					return currDict ? (currDict[key] ? this.stringFormatter(currDict[key], args) : '[' + key + ']') : '';
				}));
		}
	}
	
	public transformLookup(key: string): Observable<string> {
		return this.intlServicePP.getUsedLanguageObservable().pipe(mergeMap(lang => {
			if (lang) {
				if (!this.dictionary[lang.languageKey.toString()]) {
					this.dictionary[lang.languageKey.toString()] = {};
				}
				const currDict = this.dictionary[lang.languageKey.toString()];
				if (currDict.hasOwnProperty(key)) {
					return of(currDict[key]);
				} else {
					if (!this.getFromServerTimer) {
						this.getFromServerTimer = timer(5);
						this.keysFromServer = [key];
						this.waitingForServerTranslationsObs = this.getFromServerTimer.pipe(mergeMap(() => {
							const usedLang = this.intlServicePP.usedLanguage.languageKey;
							if (!this.dictionary[usedLang.toString()]) {
								this.dictionary[usedLang.toString()] = {};
							}
							const usedDict = this.dictionary[usedLang.toString()];
							return this.translateProxy.getLookupDataTranslation(usedLang, this.keysFromServer).pipe(map(res => {
									this.keysFromServer.forEach((currKey, index) => {
										usedDict[currKey] = res[index];
									});
									this.keysFromServer = [];
									return res;
								}
							));
						}));
						this.waitingForServerTranslationsObs = this.waitingForServerTranslationsObs.pipe(share());
					} else {
						if (!this.keysFromServer.includes(key)) {
							this.keysFromServer.push(key);
						}
					}
					return this.waitingForServerTranslationsObs.pipe(map(() => currDict[key]));
				}
			}}));
	}

	public translateOrgStructureDescription(keys: number[]): Observable<ITranslatedItem[]> {
		const ofKeys: Observable<string>[] = [];
		keys.forEach(k => {
			ofKeys.push(this.transformLookup(ORG_STRUCTURE_DESCRIPTION_PREFIX + k + LOOKUP_DATA_SUFFIX));
		});
		return zip(...ofKeys).pipe(map(o => {
			const res: ITranslatedItem[] = [];
			o.forEach((currO, i) => {
				if (!res.find(r => r.key === keys[i])) {
					res.push({key: keys[i], translation: currO});
				}
			});
			return res;
		}));
	}

	public stringFormatter(format: string, values: any[]): string {
		return format.replace(/{{a(\d+)}}/g, (match, i) => {
			return values[i] !== null && values[i] !== undefined ? values[i] : match;
		});
	}

	// Checks if key for translate already contains registered translate suffix
	public containsRegisteredTranslateSuffix(key: string): boolean {
		const suffixes: string[] = [ACTION_SUFFIX, LABEL_SUFFIX, LOGIN_SUFFIX, MESSAGE_SUFFIX, TITLE_SUFFIX, KIOSK_SUFFIX,
			ORG_STRUCTURE_DESCRIPTION_PREFIX, ORG_STRUCTURE_LOCATION_DESCRIPTION_PREFIX, PROCEDURE_CODE_DESCRIPTION_PREFIX,
			SPS_CODE_DESCRIPTION_PREFIX, LOOKUP_DATA_SUFFIX, KIOSK_SPS_CODE_DESCRIPTION_PREFIX];
		return !suffixes.every(suffix => key.indexOf(suffix) < 0);
	}

	private getTranslationsFromServer(lang: string): Observable<object> {
		return this.translateProxy.getTranslation(lang);
	}
}
